/**
 * SCM Page Definitions — Pure data describing all Supply Chain pages.
 * Consumed by <ScmPageRenderer /> — no JSX here, only JSON-like configs.
 *
 * Addresses 6 architecture requirements:
 *  1. DSL-driven — every column, filter, form, action is declarative
 *  2. Inventory + 3PL warehouse + transport integration
 *  3. Replenishment workflows (reorder point, safety stock)
 *  4. Brand management for distributors
 *  5. Online sales with inventory allocation/reservation
 *  6. Sales management for salespeople & customers
 */
import type { StatusDef, FormFieldDef } from '@/components/wms/wms-page-dsl';
import type { ScmPageDef } from './ScmPageRenderer';

// ─── SCM Statuses ──────────────────────────────────────────────────────────

const SCM_STATUSES: StatusDef[] = [
  // Generic
  { key: 'DRAFT',               label: 'Draft',               color: 'bg-gray-100 text-gray-600' },
  { key: 'ACTIVE',              label: 'Active',              color: 'bg-green-100 text-green-700' },
  { key: 'INACTIVE',            label: 'Inactive',            color: 'bg-gray-100 text-gray-500' },
  // Sales Orders
  { key: 'CONFIRMED',           label: 'Confirmed',           color: 'bg-blue-100 text-blue-700' },
  { key: 'PROCESSING',          label: 'Processing',          color: 'bg-yellow-100 text-yellow-700' },
  { key: 'PARTIALLY_SHIPPED',   label: 'Partial Shipped',     color: 'bg-orange-100 text-orange-700' },
  { key: 'SHIPPED',             label: 'Shipped',             color: 'bg-purple-100 text-purple-700' },
  { key: 'DELIVERED',           label: 'Delivered',           color: 'bg-green-100 text-green-700' },
  { key: 'INVOICED',            label: 'Invoiced',            color: 'bg-emerald-100 text-emerald-700' },
  { key: 'CANCELLED',           label: 'Cancelled',           color: 'bg-red-100 text-red-700' },
  { key: 'RETURNED',            label: 'Returned',            color: 'bg-red-100 text-red-600' },
  // Purchase Orders
  { key: 'SUBMITTED',           label: 'Submitted',           color: 'bg-blue-100 text-blue-700' },
  { key: 'APPROVED',            label: 'Approved',            color: 'bg-green-100 text-green-700' },
  { key: 'PARTIALLY_RECEIVED',  label: 'Partial Received',    color: 'bg-yellow-100 text-yellow-700' },
  { key: 'RECEIVED',            label: 'Received',            color: 'bg-green-100 text-green-700' },
  // Fulfillment
  { key: 'PENDING',             label: 'Pending',             color: 'bg-gray-100 text-gray-600' },
  { key: 'PICKING',             label: 'Picking',             color: 'bg-yellow-100 text-yellow-700' },
  { key: 'PICKED',              label: 'Picked',              color: 'bg-blue-100 text-blue-700' },
  { key: 'PACKING',             label: 'Packing',             color: 'bg-orange-100 text-orange-700' },
  { key: 'PACKED',              label: 'Packed',              color: 'bg-purple-100 text-purple-700' },
  // Quotation
  { key: 'SENT',                label: 'Sent',                color: 'bg-blue-100 text-blue-700' },
  { key: 'ACCEPTED',            label: 'Accepted',            color: 'bg-green-100 text-green-700' },
  { key: 'REJECTED',            label: 'Rejected',            color: 'bg-red-100 text-red-600' },
  { key: 'EXPIRED',             label: 'Expired',             color: 'bg-gray-100 text-gray-500' },
  // Inventory
  { key: 'NORMAL',              label: 'Normal',              color: 'bg-green-100 text-green-700' },
  { key: 'LOW_STOCK',           label: 'Low Stock',           color: 'bg-yellow-100 text-yellow-700' },
  { key: 'REORDER',             label: 'Reorder',             color: 'bg-orange-100 text-orange-700' },
  { key: 'OUT_OF_STOCK',        label: 'Out of Stock',        color: 'bg-red-100 text-red-700' },
  { key: 'OVERSTOCK',           label: 'Overstock',           color: 'bg-blue-100 text-blue-600' },
  { key: 'QUARANTINE',          label: 'Quarantine',          color: 'bg-red-100 text-red-700' },
  // Product
  { key: 'DISCONTINUED',        label: 'Discontinued',        color: 'bg-gray-100 text-gray-500' },
  // Stock Movement
  { key: 'COMPLETED',           label: 'Completed',           color: 'bg-green-100 text-green-700' },
  { key: 'IN_TRANSIT',          label: 'In Transit',          color: 'bg-blue-100 text-blue-700' },
];

// ─── Shared Field Helpers ──────────────────────────────────────────────────

const CHANNEL_OPTIONS = [
  { label: 'Direct Sales', value: 'direct' },
  { label: 'Online Store', value: 'online' },
  { label: 'Marketplace', value: 'marketplace' },
  { label: 'Wholesale', value: 'wholesale' },
  { label: 'Distributor', value: 'distributor' },
];

const CURRENCY_OPTIONS = [
  { label: 'SGD', value: 'SGD' },
  { label: 'USD', value: 'USD' },
  { label: 'MYR', value: 'MYR' },
  { label: 'CNY', value: 'CNY' },
  { label: 'EUR', value: 'EUR' },
];

const PAYMENT_TERM_OPTIONS = [
  { label: 'COD', value: 'COD' },
  { label: 'Net 7', value: 'NET7' },
  { label: 'Net 15', value: 'NET15' },
  { label: 'Net 30', value: 'NET30' },
  { label: 'Net 60', value: 'NET60' },
  { label: 'Prepaid', value: 'PREPAID' },
];

const UOM_OPTIONS = [
  { label: 'EA', value: 'EA' },
  { label: 'PCS', value: 'PCS' },
  { label: 'BOX', value: 'BOX' },
  { label: 'CARTON', value: 'CARTON' },
  { label: 'PALLET', value: 'PALLET' },
  { label: 'KG', value: 'KG' },
  { label: 'SET', value: 'SET' },
];

// ═══════════════════════════════════════════════════════════════════════════
// 1. DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════

export const dashboardPageDef: ScmPageDef = {
  key: 'dashboard',
  title: 'Supply Chain Dashboard',
  icon: 'LayoutDashboard',
  statuses: SCM_STATUSES,
  kpiCards: [
    { key: 'openSO',         label: 'Open Sales Orders',    icon: 'ShoppingCart',  color: 'text-blue-600',   valuePath: 'counts.openSalesOrders',   link: '/scm/sales-orders' },
    { key: 'openPO',         label: 'Open Purchase Orders', icon: 'Package',       color: 'text-purple-600', valuePath: 'counts.openPurchaseOrders', link: '/scm/purchase-orders' },
    { key: 'lowStock',       label: 'Low Stock Alerts',     icon: 'AlertTriangle', color: 'text-orange-600', valuePath: 'counts.lowStockAlerts',     link: '/scm/inventory' },
    { key: 'pendingFulfill', label: 'Pending Fulfillment',  icon: 'Truck',         color: 'text-cyan-600',   valuePath: 'counts.pendingFulfillment', link: '/scm/fulfillment' },
    { key: 'revenue',        label: 'Revenue (MTD)',        icon: 'DollarSign',    color: 'text-green-600',  valuePath: 'counts.revenueMtd' },
    { key: 'products',       label: 'Active Products',      icon: 'Layers',        color: 'text-indigo-600', valuePath: 'counts.activeProducts',     link: '/scm/products' },
  ],
  tabs: [
    {
      key: 'recentSales',
      label: 'Recent Sales',
      icon: 'ShoppingCart',
      apiEndpoint: '/scm/sales-orders',
      apiParams: { limit: 20, sort: '-created_at' },
      statusField: 'status',
      columns: [
        { field: 'order_number', label: 'Order #', width: 130, sortable: true, openDetailOnClick: true },
        { field: 'customer_name', label: 'Customer', width: 180, sortable: true },
        { field: 'channel', label: 'Channel', width: 100, format: 'badge' },
        { field: 'total_amount', label: 'Total', width: 110, format: 'currency' },
        { field: 'status', label: 'Status', width: 120, format: 'status' },
        { field: 'created_at', label: 'Date', width: 110, format: 'date' },
      ],
    },
    {
      key: 'recentPurchases',
      label: 'Recent Purchases',
      icon: 'Package',
      apiEndpoint: '/scm/purchase-orders',
      apiParams: { limit: 20, sort: '-created_at' },
      statusField: 'status',
      columns: [
        { field: 'po_number', label: 'PO #', width: 130, sortable: true },
        { field: 'supplier_name', label: 'Supplier', width: 180, sortable: true },
        { field: 'total_amount', label: 'Total', width: 110, format: 'currency' },
        { field: 'status', label: 'Status', width: 120, format: 'status' },
        { field: 'expected_date', label: 'Expected', width: 110, format: 'date' },
      ],
    },
    {
      key: 'stockAlerts',
      label: 'Stock Alerts',
      icon: 'AlertTriangle',
      apiEndpoint: '/scm/inventory',
      apiParams: { alert: 'low_stock,reorder,out_of_stock' },
      statusField: 'stock_status',
      columns: [
        { field: 'sku_code', label: 'SKU', width: 120, sortable: true },
        { field: 'product_name', label: 'Product', width: 200 },
        { field: 'brand', label: 'Brand', width: 120 },
        { field: 'qty_on_hand', label: 'On Hand', width: 80, format: 'number' },
        { field: 'qty_allocated', label: 'Allocated', width: 90, format: 'number' },
        { field: 'qty_available', label: 'Available', width: 90, format: 'number' },
        { field: 'reorder_point', label: 'Reorder Pt', width: 90, format: 'number' },
        { field: 'stock_status', label: 'Status', width: 110, format: 'status' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 2. SALES ORDERS
// ═══════════════════════════════════════════════════════════════════════════

const soLineFields: FormFieldDef[] = [
  { field: 'product_id', label: 'Product', type: 'select', required: true, optionsEndpoint: '/scm/products', optionsMap: { label: 'name', value: 'id' } },
  { field: 'sku_code', label: 'SKU', type: 'text' },
  { field: 'qty', label: 'Quantity', type: 'number', required: true },
  { field: 'uom', label: 'UOM', type: 'select', options: UOM_OPTIONS },
  { field: 'unit_price', label: 'Unit Price', type: 'number', required: true },
  { field: 'discount_pct', label: 'Discount %', type: 'number' },
  { field: 'warehouse_id', label: 'Ship From', type: 'select', optionsEndpoint: '/wms/warehouses', optionsMap: { label: 'name', value: 'id' } },
];

export const salesOrdersPageDef: ScmPageDef = {
  key: 'salesOrders',
  title: 'Sales Orders',
  icon: 'ShoppingCart',
  statuses: SCM_STATUSES,
  dialogs: [
    {
      key: 'createSO',
      title: 'New Sales Order',
      layoutColumns: 6,
      fields: [
        { field: 'customer_id', label: 'Customer', type: 'select', required: true, span: 3, optionsEndpoint: '/scm/customers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'channel', label: 'Sales Channel', type: 'select', required: true, span: 3, options: CHANNEL_OPTIONS },
        { field: 'salesperson_id', label: 'Salesperson', type: 'select', span: 3, optionsEndpoint: '/scm/salespersons', optionsMap: { label: 'name', value: 'id' } },
        { field: 'payment_terms', label: 'Payment Terms', type: 'select', span: 3, options: PAYMENT_TERM_OPTIONS },
        { field: 'currency', label: 'Currency', type: 'select', span: 2, options: CURRENCY_OPTIONS, defaultValue: 'SGD' },
        { field: 'delivery_date', label: 'Delivery Date', type: 'date', span: 2 },
        { field: 'brand_id', label: 'Brand', type: 'select', span: 2, optionsEndpoint: '/scm/brands', optionsMap: { label: 'name', value: 'id' } },
        { field: 'shipping_address', label: 'Ship To Address', type: 'textarea', span: 6 },
        { field: 'remarks', label: 'Remarks', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/sales-orders' },
      refetch: true,
    },
  ],
  tabs: [
    {
      key: 'all',
      label: 'All Orders',
      icon: 'List',
      createDialogKey: 'createSO',
      apiEndpoint: '/scm/sales-orders',
      statusField: 'status',
      detailApiPath: '/scm/sales-orders/:id',
      editFields: [
        { field: 'customer_id', label: 'Customer', type: 'select', required: true, optionsEndpoint: '/scm/customers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'channel', label: 'Channel', type: 'select', options: CHANNEL_OPTIONS },
        { field: 'salesperson_id', label: 'Salesperson', type: 'select', optionsEndpoint: '/scm/salespersons', optionsMap: { label: 'name', value: 'id' } },
        { field: 'payment_terms', label: 'Payment Terms', type: 'select', options: PAYMENT_TERM_OPTIONS },
        { field: 'currency', label: 'Currency', type: 'select', options: CURRENCY_OPTIONS },
        { field: 'delivery_date', label: 'Delivery Date', type: 'date' },
        { field: 'brand_id', label: 'Brand', type: 'select', optionsEndpoint: '/scm/brands', optionsMap: { label: 'name', value: 'id' } },
        { field: 'shipping_address', label: 'Ship To', type: 'textarea' },
        { field: 'remarks', label: 'Remarks', type: 'textarea' },
      ],
      lineAddApi: { method: 'POST', path: '/scm/sales-orders/:id/lines' },
      lineFields: soLineFields,
      lineUpdateApi: { method: 'PATCH', path: '/scm/sales-orders/:id/lines/:lineId' },
      lineEditFields: soLineFields,
      lineDeleteApi: { method: 'DELETE', path: '/scm/sales-orders/:id/lines/:lineId' },
      columns: [
        { field: 'order_number', label: 'Order #', width: 140, sortable: true, openDetailOnClick: true },
        { field: 'customer_name', label: 'Customer', width: 180, sortable: true },
        { field: 'channel', label: 'Channel', width: 110, format: 'badge' },
        { field: 'salesperson_name', label: 'Salesperson', width: 130, sortable: true },
        { field: 'brand_name', label: 'Brand', width: 120 },
        { field: 'line_count', label: 'Lines', width: 60, format: 'number' },
        { field: 'total_amount', label: 'Total', width: 110, format: 'currency', sortable: true },
        { field: 'currency', label: 'Ccy', width: 60 },
        { field: 'status', label: 'Status', width: 120, format: 'status' },
        { field: 'delivery_date', label: 'Delivery', width: 110, format: 'date', sortable: true },
        { field: 'created_at', label: 'Created', width: 110, format: 'date', sortable: true },
      ],
      rowActions: [
        { key: 'confirm', label: 'Confirm', icon: 'CheckCircle', showWhen: ['DRAFT'], api: { method: 'POST', path: '/scm/sales-orders/:id/confirm' }, toStatus: 'CONFIRMED' },
        { key: 'fulfill', label: 'Create Fulfillment', icon: 'Package', showWhen: ['CONFIRMED', 'PROCESSING'], api: { method: 'POST', path: '/scm/sales-orders/:id/fulfill' } },
        { key: 'invoice', label: 'Create Invoice', icon: 'FileText', showWhen: ['DELIVERED'], api: { method: 'POST', path: '/scm/sales-orders/:id/invoice' }, toStatus: 'INVOICED' },
        { key: 'cancel', label: 'Cancel', icon: 'XCircle', variant: 'destructive', showWhen: ['DRAFT', 'CONFIRMED'], confirm: 'Cancel this sales order?', api: { method: 'POST', path: '/scm/sales-orders/:id/cancel' }, toStatus: 'CANCELLED' },
      ],
    },
    {
      key: 'online',
      label: 'Online Orders',
      icon: 'Globe',
      apiEndpoint: '/scm/sales-orders',
      apiParams: { channel: 'online,marketplace' },
      statusField: 'status',
      columns: [
        { field: 'order_number', label: 'Order #', width: 140, sortable: true, openDetailOnClick: true },
        { field: 'customer_name', label: 'Customer', width: 180 },
        { field: 'channel', label: 'Channel', width: 110, format: 'badge' },
        { field: 'marketplace_ref', label: 'Marketplace Ref', width: 140 },
        { field: 'total_amount', label: 'Total', width: 110, format: 'currency' },
        { field: 'qty_reserved', label: 'Reserved', width: 90, format: 'number' },
        { field: 'status', label: 'Status', width: 120, format: 'status' },
        { field: 'created_at', label: 'Date', width: 110, format: 'date' },
      ],
    },
    {
      key: 'returns',
      label: 'Returns',
      icon: 'RotateCcw',
      apiEndpoint: '/scm/sales-orders',
      apiParams: { status: 'RETURNED' },
      statusField: 'status',
      columns: [
        { field: 'order_number', label: 'Order #', width: 140, sortable: true },
        { field: 'customer_name', label: 'Customer', width: 180 },
        { field: 'return_reason', label: 'Reason', width: 200 },
        { field: 'total_amount', label: 'Amount', width: 110, format: 'currency' },
        { field: 'returned_at', label: 'Returned', width: 110, format: 'date' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 3. PURCHASE ORDERS
// ═══════════════════════════════════════════════════════════════════════════

const poLineFields: FormFieldDef[] = [
  { field: 'product_id', label: 'Product', type: 'select', required: true, optionsEndpoint: '/scm/products', optionsMap: { label: 'name', value: 'id' } },
  { field: 'sku_code', label: 'SKU', type: 'text' },
  { field: 'qty', label: 'Quantity', type: 'number', required: true },
  { field: 'uom', label: 'UOM', type: 'select', options: UOM_OPTIONS },
  { field: 'unit_cost', label: 'Unit Cost', type: 'number', required: true },
  { field: 'warehouse_id', label: 'Receive To', type: 'select', optionsEndpoint: '/wms/warehouses', optionsMap: { label: 'name', value: 'id' } },
];

export const purchaseOrdersPageDef: ScmPageDef = {
  key: 'purchaseOrders',
  title: 'Purchase Orders',
  icon: 'Package',
  statuses: SCM_STATUSES,
  dialogs: [
    {
      key: 'createPO',
      title: 'New Purchase Order',
      layoutColumns: 6,
      fields: [
        { field: 'supplier_id', label: 'Supplier', type: 'select', required: true, span: 3, optionsEndpoint: '/scm/suppliers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'expected_date', label: 'Expected Date', type: 'date', span: 3 },
        { field: 'currency', label: 'Currency', type: 'select', span: 2, options: CURRENCY_OPTIONS, defaultValue: 'SGD' },
        { field: 'payment_terms', label: 'Payment Terms', type: 'select', span: 2, options: PAYMENT_TERM_OPTIONS },
        { field: 'warehouse_id', label: 'Receiving Warehouse', type: 'select', span: 2, optionsEndpoint: '/wms/warehouses', optionsMap: { label: 'name', value: 'id' } },
        { field: 'remarks', label: 'Remarks', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/purchase-orders' },
      refetch: true,
    },
  ],
  tabs: [
    {
      key: 'all',
      label: 'All POs',
      icon: 'List',
      createDialogKey: 'createPO',
      apiEndpoint: '/scm/purchase-orders',
      statusField: 'status',
      detailApiPath: '/scm/purchase-orders/:id',
      editFields: [
        { field: 'supplier_id', label: 'Supplier', type: 'select', optionsEndpoint: '/scm/suppliers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'expected_date', label: 'Expected Date', type: 'date' },
        { field: 'currency', label: 'Currency', type: 'select', options: CURRENCY_OPTIONS },
        { field: 'payment_terms', label: 'Payment Terms', type: 'select', options: PAYMENT_TERM_OPTIONS },
        { field: 'warehouse_id', label: 'Receiving Warehouse', type: 'select', optionsEndpoint: '/wms/warehouses', optionsMap: { label: 'name', value: 'id' } },
        { field: 'remarks', label: 'Remarks', type: 'textarea' },
      ],
      lineAddApi: { method: 'POST', path: '/scm/purchase-orders/:id/lines' },
      lineFields: poLineFields,
      lineUpdateApi: { method: 'PATCH', path: '/scm/purchase-orders/:id/lines/:lineId' },
      lineEditFields: poLineFields,
      lineDeleteApi: { method: 'DELETE', path: '/scm/purchase-orders/:id/lines/:lineId' },
      columns: [
        { field: 'po_number', label: 'PO #', width: 140, sortable: true, openDetailOnClick: true },
        { field: 'supplier_name', label: 'Supplier', width: 180, sortable: true },
        { field: 'line_count', label: 'Lines', width: 60, format: 'number' },
        { field: 'total_amount', label: 'Total', width: 110, format: 'currency', sortable: true },
        { field: 'currency', label: 'Ccy', width: 60 },
        { field: 'received_pct', label: 'Received %', width: 100, format: 'progress' },
        { field: 'status', label: 'Status', width: 120, format: 'status' },
        { field: 'expected_date', label: 'Expected', width: 110, format: 'date', sortable: true },
        { field: 'created_at', label: 'Created', width: 110, format: 'date', sortable: true },
      ],
      rowActions: [
        { key: 'submit', label: 'Submit', icon: 'Send', showWhen: ['DRAFT'], api: { method: 'POST', path: '/scm/purchase-orders/:id/submit' }, toStatus: 'SUBMITTED' },
        { key: 'approve', label: 'Approve', icon: 'CheckCircle', showWhen: ['SUBMITTED'], api: { method: 'POST', path: '/scm/purchase-orders/:id/approve' }, toStatus: 'APPROVED' },
        { key: 'receive', label: 'Receive Goods', icon: 'PackageCheck', showWhen: ['APPROVED', 'PARTIALLY_RECEIVED'], api: { method: 'POST', path: '/scm/purchase-orders/:id/receive' } },
        { key: 'cancel', label: 'Cancel', icon: 'XCircle', variant: 'destructive', showWhen: ['DRAFT', 'SUBMITTED'], confirm: 'Cancel this purchase order?', api: { method: 'POST', path: '/scm/purchase-orders/:id/cancel' }, toStatus: 'CANCELLED' },
      ],
    },
    {
      key: 'replenishment',
      label: 'Auto-Replenishment',
      icon: 'RefreshCcw',
      apiEndpoint: '/scm/purchase-orders',
      apiParams: { source: 'replenishment' },
      statusField: 'status',
      emptyMessage: 'No auto-replenishment POs. Configure reorder points in Inventory.',
      columns: [
        { field: 'po_number', label: 'PO #', width: 140, sortable: true },
        { field: 'supplier_name', label: 'Supplier', width: 180 },
        { field: 'trigger_sku', label: 'Trigger SKU', width: 140 },
        { field: 'trigger_reason', label: 'Reason', width: 150 },
        { field: 'total_amount', label: 'Total', width: 110, format: 'currency' },
        { field: 'status', label: 'Status', width: 120, format: 'status' },
        { field: 'created_at', label: 'Created', width: 110, format: 'date' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 4. PRODUCTS (Brand Management, Variants, Channels)
// ═══════════════════════════════════════════════════════════════════════════

export const productsPageDef: ScmPageDef = {
  key: 'products',
  title: 'Products',
  icon: 'Layers',
  statuses: SCM_STATUSES,
  dialogs: [
    {
      key: 'createProduct',
      title: 'New Product',
      layoutColumns: 6,
      fields: [
        { field: 'name', label: 'Product Name', type: 'text', required: true, span: 4 },
        { field: 'sku_code', label: 'SKU Code', type: 'text', required: true, span: 2 },
        { field: 'brand_id', label: 'Brand', type: 'select', span: 3, optionsEndpoint: '/scm/brands', optionsMap: { label: 'name', value: 'id' } },
        { field: 'category_id', label: 'Category', type: 'select', span: 3, optionsEndpoint: '/scm/categories', optionsMap: { label: 'name', value: 'id' } },
        { field: 'base_price', label: 'Base Price', type: 'number', required: true, span: 2 },
        { field: 'cost_price', label: 'Cost Price', type: 'number', span: 2 },
        { field: 'currency', label: 'Currency', type: 'select', span: 2, options: CURRENCY_OPTIONS, defaultValue: 'SGD' },
        { field: 'uom', label: 'UOM', type: 'select', span: 2, options: UOM_OPTIONS, defaultValue: 'EA' },
        { field: 'weight_kg', label: 'Weight (kg)', type: 'number', span: 2 },
        { field: 'hs_code', label: 'HS Code', type: 'text', span: 2 },
        { field: 'sales_channels', label: 'Sales Channels', type: 'multiselect', span: 6, options: CHANNEL_OPTIONS },
        { field: 'description', label: 'Description', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/products' },
      refetch: true,
    },
    {
      key: 'createBrand',
      title: 'New Brand',
      layoutColumns: 6,
      fields: [
        { field: 'name', label: 'Brand Name', type: 'text', required: true, span: 3 },
        { field: 'code', label: 'Brand Code', type: 'text', required: true, span: 3 },
        { field: 'country_of_origin', label: 'Country of Origin', type: 'text', span: 3 },
        { field: 'distributor_id', label: 'Distributor', type: 'select', span: 3, optionsEndpoint: '/scm/suppliers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'description', label: 'Description', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/brands' },
      refetch: true,
    },
  ],
  tabs: [
    {
      key: 'catalog',
      label: 'Product Catalog',
      icon: 'Package',
      createDialogKey: 'createProduct',
      apiEndpoint: '/scm/products',
      statusField: 'status',
      detailApiPath: '/scm/products/:id',
      editFields: [
        { field: 'name', label: 'Product Name', type: 'text', required: true },
        { field: 'sku_code', label: 'SKU', type: 'text', required: true },
        { field: 'brand_id', label: 'Brand', type: 'select', optionsEndpoint: '/scm/brands', optionsMap: { label: 'name', value: 'id' } },
        { field: 'category_id', label: 'Category', type: 'select', optionsEndpoint: '/scm/categories', optionsMap: { label: 'name', value: 'id' } },
        { field: 'base_price', label: 'Base Price', type: 'number' },
        { field: 'cost_price', label: 'Cost Price', type: 'number' },
        { field: 'uom', label: 'UOM', type: 'select', options: UOM_OPTIONS },
        { field: 'weight_kg', label: 'Weight (kg)', type: 'number' },
        { field: 'hs_code', label: 'HS Code', type: 'text' },
        { field: 'sales_channels', label: 'Sales Channels', type: 'multiselect', options: CHANNEL_OPTIONS },
        { field: 'status', label: 'Status', type: 'select', options: [{ label: 'Active', value: 'ACTIVE' }, { label: 'Inactive', value: 'INACTIVE' }, { label: 'Discontinued', value: 'DISCONTINUED' }] },
        { field: 'description', label: 'Description', type: 'textarea' },
      ],
      columns: [
        { field: 'sku_code', label: 'SKU', width: 120, sortable: true, openDetailOnClick: true },
        { field: 'name', label: 'Product', width: 220, sortable: true },
        { field: 'brand_name', label: 'Brand', width: 120, sortable: true },
        { field: 'category_name', label: 'Category', width: 130 },
        { field: 'base_price', label: 'Price', width: 100, format: 'currency' },
        { field: 'cost_price', label: 'Cost', width: 100, format: 'currency' },
        { field: 'qty_on_hand', label: 'On Hand', width: 80, format: 'number' },
        { field: 'sales_channels', label: 'Channels', width: 140, format: 'badge' },
        { field: 'status', label: 'Status', width: 110, format: 'status' },
      ],
      rowActions: [
        { key: 'activate', label: 'Activate', icon: 'CheckCircle', showWhen: ['INACTIVE', 'DISCONTINUED'], api: { method: 'PATCH', path: '/scm/products/:id', body: { status: 'ACTIVE' } }, toStatus: 'ACTIVE' },
        { key: 'deactivate', label: 'Deactivate', icon: 'PauseCircle', showWhen: ['ACTIVE'], api: { method: 'PATCH', path: '/scm/products/:id', body: { status: 'INACTIVE' } }, toStatus: 'INACTIVE' },
      ],
    },
    {
      key: 'brands',
      label: 'Brands',
      icon: 'Tag',
      createDialogKey: 'createBrand',
      apiEndpoint: '/scm/brands',
      statusField: 'status',
      detailApiPath: '/scm/brands/:id',
      editFields: [
        { field: 'name', label: 'Brand Name', type: 'text', required: true },
        { field: 'code', label: 'Code', type: 'text' },
        { field: 'country_of_origin', label: 'Origin', type: 'text' },
        { field: 'distributor_id', label: 'Distributor', type: 'select', optionsEndpoint: '/scm/suppliers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'product_count', label: 'Products', type: 'number' },
        { field: 'description', label: 'Description', type: 'textarea' },
      ],
      columns: [
        { field: 'code', label: 'Code', width: 100, sortable: true, openDetailOnClick: true },
        { field: 'name', label: 'Brand', width: 200, sortable: true },
        { field: 'country_of_origin', label: 'Origin', width: 120 },
        { field: 'distributor_name', label: 'Distributor', width: 180 },
        { field: 'product_count', label: 'Products', width: 90, format: 'number' },
        { field: 'status', label: 'Status', width: 100, format: 'status' },
      ],
    },
    {
      key: 'categories',
      label: 'Categories',
      icon: 'FolderTree',
      apiEndpoint: '/scm/categories',
      columns: [
        { field: 'name', label: 'Category', width: 200, sortable: true },
        { field: 'parent_name', label: 'Parent', width: 160 },
        { field: 'product_count', label: 'Products', width: 90, format: 'number' },
        { field: 'sort_order', label: 'Order', width: 70, format: 'number' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 5. INVENTORY (Allocation, Reservation, Replenishment)
// ═══════════════════════════════════════════════════════════════════════════

export const inventoryPageDef: ScmPageDef = {
  key: 'inventory',
  title: 'Inventory',
  icon: 'Warehouse',
  statuses: SCM_STATUSES,
  kpiCards: [
    { key: 'totalSKUs',     label: 'Total SKUs',        icon: 'Package',       color: 'text-blue-600',   valuePath: 'counts.totalSkus' },
    { key: 'totalValue',    label: 'Inventory Value',    icon: 'DollarSign',    color: 'text-green-600',  valuePath: 'counts.totalValue' },
    { key: 'lowStock',      label: 'Low Stock',          icon: 'AlertTriangle', color: 'text-orange-600', valuePath: 'counts.lowStock' },
    { key: 'outOfStock',    label: 'Out of Stock',       icon: 'XOctagon',      color: 'text-red-600',    valuePath: 'counts.outOfStock' },
  ],
  dialogs: [
    {
      key: 'adjustStock',
      title: 'Stock Adjustment',
      layoutColumns: 6,
      fields: [
        { field: 'product_id', label: 'Product', type: 'select', required: true, span: 3, optionsEndpoint: '/scm/products', optionsMap: { label: 'name', value: 'id' } },
        { field: 'warehouse_id', label: 'Warehouse', type: 'select', required: true, span: 3, optionsEndpoint: '/wms/warehouses', optionsMap: { label: 'name', value: 'id' } },
        { field: 'adjustment_type', label: 'Type', type: 'select', required: true, span: 3, options: [
          { label: 'Add Stock', value: 'ADD' },
          { label: 'Remove Stock', value: 'REMOVE' },
          { label: 'Count Correction', value: 'COUNT' },
          { label: 'Damage Write-off', value: 'WRITEOFF' },
        ]},
        { field: 'qty', label: 'Quantity', type: 'number', required: true, span: 3 },
        { field: 'reason', label: 'Reason', type: 'textarea', required: true, span: 6 },
      ],
      api: { method: 'POST', path: '/scm/inventory/adjustments' },
      refetch: true,
    },
    {
      key: 'setReplenishment',
      title: 'Set Replenishment Rules',
      layoutColumns: 6,
      fields: [
        { field: 'product_id', label: 'Product', type: 'select', required: true, span: 3, optionsEndpoint: '/scm/products', optionsMap: { label: 'name', value: 'id' } },
        { field: 'warehouse_id', label: 'Warehouse', type: 'select', required: true, span: 3, optionsEndpoint: '/wms/warehouses', optionsMap: { label: 'name', value: 'id' } },
        { field: 'reorder_point', label: 'Reorder Point', type: 'number', required: true, span: 2 },
        { field: 'safety_stock', label: 'Safety Stock', type: 'number', required: true, span: 2 },
        { field: 'reorder_qty', label: 'Reorder Quantity', type: 'number', required: true, span: 2 },
        { field: 'preferred_supplier_id', label: 'Preferred Supplier', type: 'select', span: 3, optionsEndpoint: '/scm/suppliers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'auto_po', label: 'Auto-create PO', type: 'select', span: 3, options: [{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }], defaultValue: 'false' },
      ],
      api: { method: 'POST', path: '/scm/inventory/replenishment-rules' },
      refetch: true,
    },
    {
      key: 'reserveStock',
      title: 'Reserve Inventory',
      layoutColumns: 6,
      fields: [
        { field: 'product_id', label: 'Product', type: 'select', required: true, span: 3, optionsEndpoint: '/scm/products', optionsMap: { label: 'name', value: 'id' } },
        { field: 'warehouse_id', label: 'Warehouse', type: 'select', required: true, span: 3, optionsEndpoint: '/wms/warehouses', optionsMap: { label: 'name', value: 'id' } },
        { field: 'qty', label: 'Reserve Qty', type: 'number', required: true, span: 3 },
        { field: 'reference_type', label: 'Reference Type', type: 'select', span: 3, options: [
          { label: 'Sales Order', value: 'SO' },
          { label: 'Pre-order', value: 'PREORDER' },
          { label: 'Online Hold', value: 'ONLINE_HOLD' },
          { label: 'Manual', value: 'MANUAL' },
        ]},
        { field: 'reference_id', label: 'Reference #', type: 'text', span: 3 },
        { field: 'expires_at', label: 'Reservation Expires', type: 'date', span: 3 },
        { field: 'reason', label: 'Reason', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/inventory/reservations' },
      refetch: true,
    },
  ],
  tabs: [
    {
      key: 'stock',
      label: 'Stock Positions',
      icon: 'BarChart3',
      createDialogKeys: ['adjustStock', 'setReplenishment', 'reserveStock'],
      apiEndpoint: '/scm/inventory',
      statusField: 'stock_status',
      kpiCards: [
        { key: 'totalSKUs', label: 'Total SKUs', icon: 'Package', color: 'text-blue-600', valuePath: '_computed.totalSkus' },
        { key: 'totalValue', label: 'Value', icon: 'DollarSign', color: 'text-green-600', valuePath: '_computed.totalValue' },
      ],
      columns: [
        { field: 'sku_code', label: 'SKU', width: 120, sortable: true, openDetailOnClick: true },
        { field: 'product_name', label: 'Product', width: 200, sortable: true },
        { field: 'brand_name', label: 'Brand', width: 120, sortable: true },
        { field: 'warehouse_name', label: 'Warehouse', width: 140 },
        { field: 'qty_on_hand', label: 'On Hand', width: 80, format: 'number', sortable: true },
        { field: 'qty_allocated', label: 'Allocated', width: 90, format: 'number' },
        { field: 'qty_reserved', label: 'Reserved', width: 90, format: 'number' },
        { field: 'qty_available', label: 'Available', width: 90, format: 'number', sortable: true },
        { field: 'reorder_point', label: 'Reorder Pt', width: 90, format: 'number' },
        { field: 'safety_stock', label: 'Safety Stock', width: 90, format: 'number' },
        { field: 'unit_cost', label: 'Unit Cost', width: 90, format: 'currency' },
        { field: 'total_value', label: 'Value', width: 100, format: 'currency' },
        { field: 'stock_status', label: 'Status', width: 110, format: 'status' },
      ],
      rowActions: [
        { key: 'reorder', label: 'Create PO', icon: 'ShoppingCart', showWhen: ['LOW_STOCK', 'REORDER', 'OUT_OF_STOCK'], api: { method: 'POST', path: '/scm/inventory/:id/reorder' } },
      ],
    },
    {
      key: 'reservations',
      label: 'Reservations',
      icon: 'Lock',
      createDialogKey: 'reserveStock',
      apiEndpoint: '/scm/inventory/reservations',
      statusField: 'status',
      columns: [
        { field: 'reservation_number', label: 'Reservation #', width: 140, sortable: true },
        { field: 'product_name', label: 'Product', width: 200 },
        { field: 'sku_code', label: 'SKU', width: 120 },
        { field: 'warehouse_name', label: 'Warehouse', width: 140 },
        { field: 'qty', label: 'Qty', width: 70, format: 'number' },
        { field: 'reference_type', label: 'Ref Type', width: 100, format: 'badge' },
        { field: 'reference_id', label: 'Ref #', width: 130 },
        { field: 'expires_at', label: 'Expires', width: 110, format: 'date' },
        { field: 'status', label: 'Status', width: 100, format: 'status' },
      ],
      rowActions: [
        { key: 'release', label: 'Release', icon: 'Unlock', showWhen: ['ACTIVE'], confirm: 'Release this reservation?', api: { method: 'POST', path: '/scm/inventory/reservations/:id/release' }, toStatus: 'COMPLETED' },
      ],
    },
    {
      key: 'replenishment',
      label: 'Replenishment Rules',
      icon: 'RefreshCcw',
      createDialogKey: 'setReplenishment',
      apiEndpoint: '/scm/inventory/replenishment-rules',
      detailApiPath: '/scm/inventory/replenishment-rules/:id',
      editFields: [
        { field: 'reorder_point', label: 'Reorder Point', type: 'number', required: true },
        { field: 'safety_stock', label: 'Safety Stock', type: 'number', required: true },
        { field: 'reorder_qty', label: 'Reorder Qty', type: 'number', required: true },
        { field: 'preferred_supplier_id', label: 'Preferred Supplier', type: 'select', optionsEndpoint: '/scm/suppliers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'auto_po', label: 'Auto PO', type: 'select', options: [{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }] },
      ],
      columns: [
        { field: 'sku_code', label: 'SKU', width: 120, sortable: true },
        { field: 'product_name', label: 'Product', width: 200 },
        { field: 'warehouse_name', label: 'Warehouse', width: 140 },
        { field: 'reorder_point', label: 'Reorder Pt', width: 90, format: 'number' },
        { field: 'safety_stock', label: 'Safety Stock', width: 90, format: 'number' },
        { field: 'reorder_qty', label: 'Reorder Qty', width: 90, format: 'number' },
        { field: 'preferred_supplier_name', label: 'Supplier', width: 150 },
        { field: 'auto_po', label: 'Auto PO', width: 80, format: 'badge' },
        { field: 'current_stock', label: 'Current', width: 80, format: 'number' },
        { field: 'stock_status', label: 'Status', width: 100, format: 'status' },
      ],
    },
    {
      key: 'channelAllocation',
      label: 'Channel Allocation',
      icon: 'GitBranch',
      apiEndpoint: '/scm/inventory/channel-allocations',
      emptyMessage: 'Configure channel-specific inventory allocations for online and marketplace orders.',
      columns: [
        { field: 'sku_code', label: 'SKU', width: 120, sortable: true },
        { field: 'product_name', label: 'Product', width: 200 },
        { field: 'channel', label: 'Channel', width: 120, format: 'badge' },
        { field: 'allocated_qty', label: 'Allocated', width: 90, format: 'number' },
        { field: 'sold_qty', label: 'Sold', width: 80, format: 'number' },
        { field: 'remaining_qty', label: 'Remaining', width: 90, format: 'number' },
        { field: 'updated_at', label: 'Updated', width: 110, format: 'datetime' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 6. QUOTATIONS
// ═══════════════════════════════════════════════════════════════════════════

const quotationLineFields: FormFieldDef[] = [
  { field: 'product_id', label: 'Product', type: 'select', required: true, optionsEndpoint: '/scm/products', optionsMap: { label: 'name', value: 'id' } },
  { field: 'qty', label: 'Quantity', type: 'number', required: true },
  { field: 'unit_price', label: 'Unit Price', type: 'number', required: true },
  { field: 'discount_pct', label: 'Discount %', type: 'number' },
  { field: 'remarks', label: 'Remarks', type: 'text' },
];

export const quotationsPageDef: ScmPageDef = {
  key: 'quotations',
  title: 'Quotations',
  icon: 'FileText',
  statuses: SCM_STATUSES,
  dialogs: [
    {
      key: 'createQuotation',
      title: 'New Quotation',
      layoutColumns: 6,
      fields: [
        { field: 'customer_id', label: 'Customer', type: 'select', required: true, span: 3, optionsEndpoint: '/scm/customers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'salesperson_id', label: 'Salesperson', type: 'select', span: 3, optionsEndpoint: '/scm/salespersons', optionsMap: { label: 'name', value: 'id' } },
        { field: 'currency', label: 'Currency', type: 'select', span: 2, options: CURRENCY_OPTIONS, defaultValue: 'SGD' },
        { field: 'valid_until', label: 'Valid Until', type: 'date', span: 2 },
        { field: 'payment_terms', label: 'Terms', type: 'select', span: 2, options: PAYMENT_TERM_OPTIONS },
        { field: 'remarks', label: 'Remarks', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/quotations' },
      refetch: true,
    },
  ],
  tabs: [
    {
      key: 'all',
      label: 'All Quotations',
      icon: 'List',
      createDialogKey: 'createQuotation',
      apiEndpoint: '/scm/quotations',
      statusField: 'status',
      detailApiPath: '/scm/quotations/:id',
      lineAddApi: { method: 'POST', path: '/scm/quotations/:id/lines' },
      lineFields: quotationLineFields,
      lineUpdateApi: { method: 'PATCH', path: '/scm/quotations/:id/lines/:lineId' },
      lineEditFields: quotationLineFields,
      lineDeleteApi: { method: 'DELETE', path: '/scm/quotations/:id/lines/:lineId' },
      columns: [
        { field: 'quotation_number', label: 'Quote #', width: 140, sortable: true, openDetailOnClick: true },
        { field: 'customer_name', label: 'Customer', width: 180, sortable: true },
        { field: 'salesperson_name', label: 'Salesperson', width: 130 },
        { field: 'total_amount', label: 'Total', width: 110, format: 'currency' },
        { field: 'valid_until', label: 'Valid Until', width: 110, format: 'date' },
        { field: 'status', label: 'Status', width: 110, format: 'status' },
        { field: 'created_at', label: 'Created', width: 110, format: 'date', sortable: true },
      ],
      rowActions: [
        { key: 'send', label: 'Send', icon: 'Send', showWhen: ['DRAFT'], api: { method: 'POST', path: '/scm/quotations/:id/send' }, toStatus: 'SENT' },
        { key: 'convertToSO', label: 'Convert to SO', icon: 'ArrowRight', showWhen: ['ACCEPTED'], api: { method: 'POST', path: '/scm/quotations/:id/convert' } },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 7. CUSTOMERS (CRM / Sales Management)
// ═══════════════════════════════════════════════════════════════════════════

export const customersPageDef: ScmPageDef = {
  key: 'customers',
  title: 'Customers',
  icon: 'Users',
  statuses: SCM_STATUSES,
  dialogs: [
    {
      key: 'createCustomer',
      title: 'New Customer',
      layoutColumns: 6,
      fields: [
        { field: 'name', label: 'Company Name', type: 'text', required: true, span: 4 },
        { field: 'code', label: 'Customer Code', type: 'text', span: 2 },
        { field: 'contact_person', label: 'Contact Person', type: 'text', span: 3 },
        { field: 'email', label: 'Email', type: 'text', span: 3 },
        { field: 'phone', label: 'Phone', type: 'text', span: 3 },
        { field: 'salesperson_id', label: 'Salesperson', type: 'select', span: 3, optionsEndpoint: '/scm/salespersons', optionsMap: { label: 'name', value: 'id' } },
        { field: 'payment_terms', label: 'Payment Terms', type: 'select', span: 3, options: PAYMENT_TERM_OPTIONS },
        { field: 'credit_limit', label: 'Credit Limit', type: 'number', span: 3 },
        { field: 'currency', label: 'Currency', type: 'select', span: 2, options: CURRENCY_OPTIONS, defaultValue: 'SGD' },
        { field: 'customer_type', label: 'Type', type: 'select', span: 2, options: [
          { label: 'Direct', value: 'DIRECT' },
          { label: 'Distributor', value: 'DISTRIBUTOR' },
          { label: 'Online', value: 'ONLINE' },
          { label: 'Wholesale', value: 'WHOLESALE' },
        ]},
        { field: 'tax_id', label: 'Tax ID / UEN', type: 'text', span: 2 },
        { field: 'address', label: 'Address', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/customers' },
      refetch: true,
    },
  ],
  tabs: [
    {
      key: 'all',
      label: 'All Customers',
      icon: 'Users',
      createDialogKey: 'createCustomer',
      apiEndpoint: '/scm/customers',
      statusField: 'status',
      detailApiPath: '/scm/customers/:id',
      editMode: 'both',
      updateApi: { method: 'PATCH', path: '/scm/customers/:id' },
      deleteApi: { method: 'DELETE', path: '/scm/customers/:id' },
      editFields: [
        { field: 'name', label: 'Company', type: 'text', required: true },
        { field: 'code', label: 'Code', type: 'text' },
        { field: 'contact_person', label: 'Contact', type: 'text' },
        { field: 'email', label: 'Email', type: 'text' },
        { field: 'phone', label: 'Phone', type: 'text' },
        { field: 'salesperson_id', label: 'Salesperson', type: 'select', optionsEndpoint: '/scm/salespersons', optionsMap: { label: 'name', value: 'id' } },
        { field: 'payment_terms', label: 'Terms', type: 'select', options: PAYMENT_TERM_OPTIONS },
        { field: 'credit_limit', label: 'Credit Limit', type: 'number' },
        { field: 'currency', label: 'Currency', type: 'select', options: CURRENCY_OPTIONS },
        { field: 'customer_type', label: 'Type', type: 'select', options: [{ label: 'Direct', value: 'DIRECT' }, { label: 'Distributor', value: 'DISTRIBUTOR' }, { label: 'Online', value: 'ONLINE' }, { label: 'Wholesale', value: 'WHOLESALE' }] },
        { field: 'tax_id', label: 'Tax ID', type: 'text' },
        { field: 'address', label: 'Address', type: 'textarea' },
      ],
      columns: [
        { field: 'code', label: 'Code', width: 100, sortable: true, openDetailOnClick: true },
        { field: 'name', label: 'Company', width: 200, sortable: true },
        { field: 'contact_person', label: 'Contact', width: 140 },
        { field: 'email', label: 'Email', width: 180 },
        { field: 'customer_type', label: 'Type', width: 110, format: 'badge' },
        { field: 'salesperson_name', label: 'Salesperson', width: 130 },
        { field: 'credit_limit', label: 'Credit Limit', width: 110, format: 'currency' },
        { field: 'outstanding_amount', label: 'Outstanding', width: 110, format: 'currency' },
        { field: 'total_orders', label: 'Orders', width: 70, format: 'number' },
        { field: 'status', label: 'Status', width: 100, format: 'status' },
      ],
    },
    {
      key: 'salespersons',
      label: 'Salespersons',
      icon: 'UserCheck',
      apiEndpoint: '/scm/salespersons',
      statusField: 'status',
      columns: [
        { field: 'name', label: 'Name', width: 180, sortable: true },
        { field: 'email', label: 'Email', width: 200 },
        { field: 'phone', label: 'Phone', width: 130 },
        { field: 'customer_count', label: 'Customers', width: 90, format: 'number' },
        { field: 'open_orders', label: 'Open Orders', width: 100, format: 'number' },
        { field: 'revenue_mtd', label: 'Revenue (MTD)', width: 120, format: 'currency' },
        { field: 'commission_rate', label: 'Commission %', width: 100, format: 'number' },
        { field: 'status', label: 'Status', width: 100, format: 'status' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 8. SUPPLIERS
// ═══════════════════════════════════════════════════════════════════════════

export const suppliersPageDef: ScmPageDef = {
  key: 'suppliers',
  title: 'Suppliers',
  icon: 'Factory',
  statuses: SCM_STATUSES,
  dialogs: [
    {
      key: 'createSupplier',
      title: 'New Supplier',
      layoutColumns: 6,
      fields: [
        { field: 'name', label: 'Supplier Name', type: 'text', required: true, span: 4 },
        { field: 'code', label: 'Supplier Code', type: 'text', span: 2 },
        { field: 'contact_person', label: 'Contact Person', type: 'text', span: 3 },
        { field: 'email', label: 'Email', type: 'text', span: 3 },
        { field: 'phone', label: 'Phone', type: 'text', span: 3 },
        { field: 'payment_terms', label: 'Payment Terms', type: 'select', span: 3, options: PAYMENT_TERM_OPTIONS },
        { field: 'currency', label: 'Currency', type: 'select', span: 2, options: CURRENCY_OPTIONS, defaultValue: 'SGD' },
        { field: 'lead_time_days', label: 'Lead Time (days)', type: 'number', span: 2 },
        { field: 'country', label: 'Country', type: 'text', span: 2 },
        { field: 'address', label: 'Address', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/suppliers' },
      refetch: true,
    },
  ],
  tabs: [
    {
      key: 'all',
      label: 'All Suppliers',
      icon: 'List',
      createDialogKey: 'createSupplier',
      apiEndpoint: '/scm/suppliers',
      statusField: 'status',
      detailApiPath: '/scm/suppliers/:id',
      editFields: [
        { field: 'name', label: 'Name', type: 'text', required: true },
        { field: 'code', label: 'Code', type: 'text' },
        { field: 'contact_person', label: 'Contact', type: 'text' },
        { field: 'email', label: 'Email', type: 'text' },
        { field: 'phone', label: 'Phone', type: 'text' },
        { field: 'payment_terms', label: 'Terms', type: 'select', options: PAYMENT_TERM_OPTIONS },
        { field: 'currency', label: 'Currency', type: 'select', options: CURRENCY_OPTIONS },
        { field: 'lead_time_days', label: 'Lead Time', type: 'number' },
        { field: 'country', label: 'Country', type: 'text' },
        { field: 'address', label: 'Address', type: 'textarea' },
      ],
      columns: [
        { field: 'code', label: 'Code', width: 100, sortable: true, openDetailOnClick: true },
        { field: 'name', label: 'Supplier', width: 200, sortable: true },
        { field: 'contact_person', label: 'Contact', width: 140 },
        { field: 'country', label: 'Country', width: 100 },
        { field: 'lead_time_days', label: 'Lead Time', width: 90, format: 'number' },
        { field: 'open_po_count', label: 'Open POs', width: 90, format: 'number' },
        { field: 'total_spent', label: 'Total Spent', width: 110, format: 'currency' },
        { field: 'rating', label: 'Rating', width: 80, format: 'number' },
        { field: 'status', label: 'Status', width: 100, format: 'status' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 9. RFQ (Request for Quotation)
// ═══════════════════════════════════════════════════════════════════════════

export const rfqPageDef: ScmPageDef = {
  key: 'rfq',
  title: 'Request for Quotation',
  icon: 'MessageSquare',
  statuses: SCM_STATUSES,
  dialogs: [
    {
      key: 'createRFQ',
      title: 'New RFQ',
      layoutColumns: 6,
      fields: [
        { field: 'supplier_ids', label: 'Suppliers', type: 'multiselect', required: true, span: 6, optionsEndpoint: '/scm/suppliers', optionsMap: { label: 'name', value: 'id' } },
        { field: 'deadline', label: 'Response Deadline', type: 'date', required: true, span: 3 },
        { field: 'currency', label: 'Currency', type: 'select', span: 3, options: CURRENCY_OPTIONS, defaultValue: 'SGD' },
        { field: 'description', label: 'Description', type: 'textarea', span: 6 },
      ],
      api: { method: 'POST', path: '/scm/rfqs' },
      refetch: true,
    },
  ],
  tabs: [
    {
      key: 'all',
      label: 'All RFQs',
      icon: 'List',
      createDialogKey: 'createRFQ',
      apiEndpoint: '/scm/rfqs',
      statusField: 'status',
      detailApiPath: '/scm/rfqs/:id',
      columns: [
        { field: 'rfq_number', label: 'RFQ #', width: 130, sortable: true, openDetailOnClick: true },
        { field: 'description', label: 'Description', width: 250 },
        { field: 'supplier_count', label: 'Suppliers', width: 90, format: 'number' },
        { field: 'response_count', label: 'Responses', width: 90, format: 'number' },
        { field: 'deadline', label: 'Deadline', width: 110, format: 'date' },
        { field: 'best_price', label: 'Best Price', width: 110, format: 'currency' },
        { field: 'status', label: 'Status', width: 110, format: 'status' },
        { field: 'created_at', label: 'Created', width: 110, format: 'date', sortable: true },
      ],
      rowActions: [
        { key: 'send', label: 'Send to Suppliers', icon: 'Send', showWhen: ['DRAFT'], api: { method: 'POST', path: '/scm/rfqs/:id/send' }, toStatus: 'SENT' },
        { key: 'award', label: 'Award & Create PO', icon: 'Award', showWhen: ['SENT'], api: { method: 'POST', path: '/scm/rfqs/:id/award' } },
        { key: 'close', label: 'Close', icon: 'XCircle', showWhen: ['SENT'], api: { method: 'POST', path: '/scm/rfqs/:id/close' }, toStatus: 'COMPLETED' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 10. STOCK MOVEMENTS
// ═══════════════════════════════════════════════════════════════════════════

export const stockMovementsPageDef: ScmPageDef = {
  key: 'stockMovements',
  title: 'Stock Movements',
  icon: 'ArrowRightLeft',
  statuses: SCM_STATUSES,
  tabs: [
    {
      key: 'all',
      label: 'All Movements',
      icon: 'List',
      apiEndpoint: '/scm/stock-movements',
      statusField: 'status',
      columns: [
        { field: 'movement_number', label: 'Movement #', width: 140, sortable: true },
        { field: 'movement_type', label: 'Type', width: 120, format: 'badge' },
        { field: 'sku_code', label: 'SKU', width: 120, sortable: true },
        { field: 'product_name', label: 'Product', width: 200 },
        { field: 'from_warehouse', label: 'From', width: 130 },
        { field: 'to_warehouse', label: 'To', width: 130 },
        { field: 'qty', label: 'Qty', width: 70, format: 'number' },
        { field: 'reference_type', label: 'Ref Type', width: 90 },
        { field: 'reference_number', label: 'Ref #', width: 130 },
        { field: 'status', label: 'Status', width: 100, format: 'status' },
        { field: 'created_at', label: 'Date', width: 110, format: 'datetime', sortable: true },
      ],
    },
    {
      key: 'adjustments',
      label: 'Adjustments',
      icon: 'PenTool',
      apiEndpoint: '/scm/stock-movements',
      apiParams: { type: 'ADJUSTMENT' },
      columns: [
        { field: 'movement_number', label: '#', width: 140, sortable: true },
        { field: 'sku_code', label: 'SKU', width: 120 },
        { field: 'product_name', label: 'Product', width: 200 },
        { field: 'warehouse_name', label: 'Warehouse', width: 140 },
        { field: 'qty', label: 'Adj Qty', width: 80, format: 'number' },
        { field: 'reason', label: 'Reason', width: 200 },
        { field: 'created_by', label: 'By', width: 120 },
        { field: 'created_at', label: 'Date', width: 110, format: 'datetime' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 11. FULFILLMENT (Pick / Pack / Ship)
// ═══════════════════════════════════════════════════════════════════════════

export const fulfillmentPageDef: ScmPageDef = {
  key: 'fulfillment',
  title: 'Fulfillment',
  icon: 'Truck',
  statuses: SCM_STATUSES,
  kpiCards: [
    { key: 'pending',  label: 'Pending',    icon: 'Clock',     color: 'text-gray-600',   valuePath: 'counts.pending' },
    { key: 'picking',  label: 'Picking',    icon: 'Package',   color: 'text-yellow-600', valuePath: 'counts.picking' },
    { key: 'packing',  label: 'Packing',    icon: 'BoxSelect', color: 'text-orange-600', valuePath: 'counts.packing' },
    { key: 'shipped',  label: 'Shipped',    icon: 'Truck',     color: 'text-blue-600',   valuePath: 'counts.shipped' },
  ],
  tabs: [
    {
      key: 'all',
      label: 'All Fulfillments',
      icon: 'List',
      apiEndpoint: '/scm/fulfillments',
      statusField: 'status',
      detailApiPath: '/scm/fulfillments/:id',
      columns: [
        { field: 'fulfillment_number', label: 'Fulfillment #', width: 150, sortable: true, openDetailOnClick: true },
        { field: 'order_number', label: 'SO #', width: 130 },
        { field: 'customer_name', label: 'Customer', width: 180 },
        { field: 'warehouse_name', label: 'Warehouse', width: 140 },
        { field: 'line_count', label: 'Items', width: 60, format: 'number' },
        { field: 'total_qty', label: 'Total Qty', width: 80, format: 'number' },
        { field: 'carrier', label: 'Carrier', width: 120 },
        { field: 'tracking_number', label: 'Tracking #', width: 150 },
        { field: 'status', label: 'Status', width: 110, format: 'status' },
        { field: 'created_at', label: 'Created', width: 110, format: 'date', sortable: true },
      ],
      rowActions: [
        { key: 'startPicking', label: 'Start Picking', icon: 'Play', showWhen: ['PENDING'], api: { method: 'POST', path: '/scm/fulfillments/:id/pick' }, toStatus: 'PICKING' },
        { key: 'completePick', label: 'Pick Done', icon: 'CheckSquare', showWhen: ['PICKING'], api: { method: 'POST', path: '/scm/fulfillments/:id/pick-done' }, toStatus: 'PICKED' },
        { key: 'startPacking', label: 'Start Packing', icon: 'Box', showWhen: ['PICKED'], api: { method: 'POST', path: '/scm/fulfillments/:id/pack' }, toStatus: 'PACKING' },
        { key: 'completePack', label: 'Pack Done', icon: 'Package', showWhen: ['PACKING'], api: { method: 'POST', path: '/scm/fulfillments/:id/pack-done' }, toStatus: 'PACKED' },
        { key: 'ship', label: 'Ship', icon: 'Truck', showWhen: ['PACKED'], api: { method: 'POST', path: '/scm/fulfillments/:id/ship' }, toStatus: 'SHIPPED' },
        { key: 'deliver', label: 'Mark Delivered', icon: 'CheckCircle2', showWhen: ['SHIPPED'], api: { method: 'POST', path: '/scm/fulfillments/:id/deliver' }, toStatus: 'DELIVERED' },
      ],
    },
    {
      key: 'pickLists',
      label: 'Pick Lists',
      icon: 'ClipboardList',
      apiEndpoint: '/scm/fulfillments/pick-lists',
      statusField: 'status',
      columns: [
        { field: 'pick_list_number', label: 'Pick List #', width: 140, sortable: true },
        { field: 'warehouse_name', label: 'Warehouse', width: 140 },
        { field: 'item_count', label: 'Items', width: 70, format: 'number' },
        { field: 'assigned_to', label: 'Assigned To', width: 130 },
        { field: 'status', label: 'Status', width: 110, format: 'status' },
        { field: 'created_at', label: 'Created', width: 110, format: 'datetime' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 11b. SALESPERSONS
// ═══════════════════════════════════════════════════════════════════════════

export const salespersonsPageDef: ScmPageDef = {
  key: 'salespersons',
  title: 'Salespersons',
  icon: 'UserCheck',
  statuses: [
    { key: 'ACTIVE', label: 'Active', color: 'green' },
    { key: 'INACTIVE', label: 'Inactive', color: 'gray' },
  ],
  tabs: [
    {
      key: 'all',
      label: 'All Salespersons',
      icon: 'Users',
      apiEndpoint: '/scm/salespersons',
      statusField: 'status',
      columns: [
        { field: 'name', label: 'Name', width: 180, sortable: true },
        { field: 'email', label: 'Email', width: 200 },
        { field: 'phone', label: 'Phone', width: 140 },
        { field: 'commission_rate', label: 'Commission %', width: 120, format: 'number' },
        { field: 'status', label: 'Status', width: 100, format: 'status' },
        { field: 'created_at', label: 'Created', width: 120, format: 'date', sortable: true },
      ],
      createDialogKey: 'salesperson',
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 12. SHIPMENT TRACKING
// ═══════════════════════════════════════════════════════════════════════════

export const shipmentTrackingPageDef: ScmPageDef = {
  key: 'shipmentTracking',
  title: 'Shipment Tracking',
  icon: 'MapPin',
  statuses: SCM_STATUSES,
  tabs: [
    {
      key: 'all',
      label: 'All Shipments',
      icon: 'List',
      apiEndpoint: '/scm/shipments',
      statusField: 'status',
      detailApiPath: '/scm/shipments/:id',
      columns: [
        { field: 'tracking_number', label: 'Tracking #', width: 160, sortable: true, openDetailOnClick: true },
        { field: 'order_number', label: 'SO #', width: 130 },
        { field: 'customer_name', label: 'Customer', width: 180 },
        { field: 'carrier', label: 'Carrier', width: 120 },
        { field: 'origin', label: 'Origin', width: 120 },
        { field: 'destination', label: 'Destination', width: 120 },
        { field: 'ship_date', label: 'Shipped', width: 110, format: 'date' },
        { field: 'eta', label: 'ETA', width: 110, format: 'date' },
        { field: 'status', label: 'Status', width: 110, format: 'status' },
      ],
    },
    {
      key: 'delayed',
      label: 'Delayed',
      icon: 'AlertTriangle',
      apiEndpoint: '/scm/shipments',
      apiParams: { delayed: 'true' },
      statusField: 'status',
      emptyMessage: 'No delayed shipments — all on track!',
      columns: [
        { field: 'tracking_number', label: 'Tracking #', width: 160, sortable: true },
        { field: 'customer_name', label: 'Customer', width: 180 },
        { field: 'carrier', label: 'Carrier', width: 120 },
        { field: 'original_eta', label: 'Original ETA', width: 110, format: 'date' },
        { field: 'revised_eta', label: 'Revised ETA', width: 110, format: 'date' },
        { field: 'delay_days', label: 'Delay (days)', width: 100, format: 'number' },
        { field: 'status', label: 'Status', width: 110, format: 'status' },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 13. COSTING
// ═══════════════════════════════════════════════════════════════════════════

export const costingPageDef: ScmPageDef = {
  key: 'costing',
  title: 'Costing & Margins',
  icon: 'Calculator',
  statuses: SCM_STATUSES,
  tabs: [
    {
      key: 'productCosting',
      label: 'Product Costing',
      icon: 'DollarSign',
      apiEndpoint: '/scm/costing/products',
      columns: [
        { field: 'sku_code', label: 'SKU', width: 120, sortable: true },
        { field: 'product_name', label: 'Product', width: 200, sortable: true },
        { field: 'brand_name', label: 'Brand', width: 120 },
        { field: 'cost_price', label: 'Cost', width: 100, format: 'currency' },
        { field: 'selling_price', label: 'Sell Price', width: 100, format: 'currency' },
        { field: 'margin_pct', label: 'Margin %', width: 90, format: 'number' },
        { field: 'landed_cost', label: 'Landed Cost', width: 110, format: 'currency' },
        { field: 'freight_cost', label: 'Freight', width: 90, format: 'currency' },
        { field: 'duty_cost', label: 'Duty', width: 90, format: 'currency' },
        { field: 'last_po_cost', label: 'Last PO Cost', width: 110, format: 'currency' },
      ],
    },
    {
      key: 'orderMargins',
      label: 'Order Margins',
      icon: 'TrendingUp',
      apiEndpoint: '/scm/costing/orders',
      columns: [
        { field: 'order_number', label: 'Order #', width: 140, sortable: true },
        { field: 'customer_name', label: 'Customer', width: 180 },
        { field: 'revenue', label: 'Revenue', width: 110, format: 'currency' },
        { field: 'cogs', label: 'COGS', width: 100, format: 'currency' },
        { field: 'gross_profit', label: 'Gross Profit', width: 110, format: 'currency' },
        { field: 'margin_pct', label: 'Margin %', width: 90, format: 'number' },
        { field: 'channel', label: 'Channel', width: 110, format: 'badge' },
        { field: 'created_at', label: 'Date', width: 110, format: 'date', sortable: true },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// 14. REPORTS
// ═══════════════════════════════════════════════════════════════════════════

export const reportsPageDef: ScmPageDef = {
  key: 'reports',
  title: 'SCM Reports',
  icon: 'BarChart3',
  statuses: SCM_STATUSES,
  tabs: [
    {
      key: 'salesSummary',
      label: 'Sales Summary',
      icon: 'TrendingUp',
      apiEndpoint: '/scm/reports/sales-summary',
      columns: [
        { field: 'period', label: 'Period', width: 120, sortable: true },
        { field: 'order_count', label: 'Orders', width: 80, format: 'number' },
        { field: 'total_revenue', label: 'Revenue', width: 120, format: 'currency' },
        { field: 'total_cogs', label: 'COGS', width: 110, format: 'currency' },
        { field: 'gross_profit', label: 'Gross Profit', width: 120, format: 'currency' },
        { field: 'margin_pct', label: 'Margin %', width: 90, format: 'number' },
        { field: 'avg_order_value', label: 'Avg Order', width: 110, format: 'currency' },
      ],
    },
    {
      key: 'topProducts',
      label: 'Top Products',
      icon: 'Star',
      apiEndpoint: '/scm/reports/top-products',
      columns: [
        { field: 'rank', label: '#', width: 40 },
        { field: 'sku_code', label: 'SKU', width: 120 },
        { field: 'product_name', label: 'Product', width: 200 },
        { field: 'brand_name', label: 'Brand', width: 120 },
        { field: 'qty_sold', label: 'Qty Sold', width: 90, format: 'number' },
        { field: 'revenue', label: 'Revenue', width: 120, format: 'currency' },
        { field: 'margin_pct', label: 'Margin %', width: 90, format: 'number' },
      ],
    },
    {
      key: 'channelPerformance',
      label: 'Channel Performance',
      icon: 'PieChart',
      apiEndpoint: '/scm/reports/channel-performance',
      columns: [
        { field: 'channel', label: 'Channel', width: 140, format: 'badge' },
        { field: 'order_count', label: 'Orders', width: 80, format: 'number' },
        { field: 'revenue', label: 'Revenue', width: 120, format: 'currency' },
        { field: 'revenue_pct', label: '% of Total', width: 90, format: 'number' },
        { field: 'avg_order_value', label: 'Avg Order', width: 110, format: 'currency' },
        { field: 'return_rate', label: 'Return %', width: 90, format: 'number' },
      ],
    },
    {
      key: 'inventoryTurnover',
      label: 'Inventory Turnover',
      icon: 'RefreshCcw',
      apiEndpoint: '/scm/reports/inventory-turnover',
      columns: [
        { field: 'sku_code', label: 'SKU', width: 120 },
        { field: 'product_name', label: 'Product', width: 200 },
        { field: 'avg_stock', label: 'Avg Stock', width: 90, format: 'number' },
        { field: 'qty_sold', label: 'Sold (Period)', width: 100, format: 'number' },
        { field: 'turnover_ratio', label: 'Turnover', width: 90, format: 'number' },
        { field: 'days_on_hand', label: 'DOH', width: 80, format: 'number' },
        { field: 'stock_value', label: 'Value', width: 110, format: 'currency' },
      ],
    },
    {
      key: 'salespersonPerformance',
      label: 'Salesperson',
      icon: 'UserCheck',
      apiEndpoint: '/scm/reports/salesperson-performance',
      columns: [
        { field: 'rank', label: '#', width: 40 },
        { field: 'salesperson_name', label: 'Salesperson', width: 180 },
        { field: 'customer_count', label: 'Customers', width: 90, format: 'number' },
        { field: 'order_count', label: 'Orders', width: 80, format: 'number' },
        { field: 'revenue', label: 'Revenue', width: 120, format: 'currency' },
        { field: 'commission', label: 'Commission', width: 110, format: 'currency' },
        { field: 'conversion_rate', label: 'Conversion %', width: 100, format: 'number' },
      ],
    },
  ],
};
